// ═══════════════════════════════════════════════════════════════════════
// Reporting (web) — rendu d'un document : mentions obligatoires, blocs de tableaux (montants alignés, ligne Total),
// contrôles (équations « Juste » / « Faux » : mot + icône + couleur, jamais la couleur seule), boutons d'export
// (Excel, PDF, HTML ; fichier tracé par le serveur, empreinte affichée). Composants définis hors de tout parent.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { CheckCircle2, Download, XCircle } from 'lucide-react';
import { Bouton, BandeauInfo } from '../../../design/composants';
import { ErreurServeur, ouvrirBlob } from '../commun';
import { francs } from '../apiSocial';
import { FORMATS, LIBELLES_ETATS } from './apiRapports';

const nombre = (v) => (v === null || v === undefined || v === '' ? '—' : String(Math.trunc(Number(v))).replace(/\B(?=(\d{3})+(?!\d))/g, ' '));
/** Valeur affichée d'une cellule selon son type (comme les exports du serveur). */
export function cellule(v, type) {
  if (type === 'montant') return v === null || v === undefined ? '—' : francs(v);
  if (type === 'nombre') return nombre(v);
  if (type === 'pourcentage') return v === null || v === undefined ? '—' : `${String(v).replace('.', ',')} %`;
  if (type === 'oui_non') return v ? 'Oui' : 'Non';
  return v === null || v === undefined || v === '' ? '—' : String(v);
}
const aDroite = (type) => type === 'montant' || type === 'nombre' || type === 'pourcentage';

/** Mentions obligatoires (simulation exclue, comptabilité de démonstration, aucune retenue transmise…). */
export function Mentions({ mentions }) {
  const l = Array.isArray(mentions) ? mentions.filter(Boolean) : [];
  if (!l.length) return null;
  return (
    <BandeauInfo ton="alerte" titre="À lire avant tout usage de ces chiffres">
      <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>{l.map((m) => <li key={m}>{m}</li>)}</ul>
    </BandeauInfo>
  );
}

export function TableauRapport({ t }) {
  if (!t.lignes.length && !t.total) {
    return (
      <div style={{ margin: '0 0 14px' }}>
        {t.titre && <h4 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600 }}>{t.titre}</h4>}
        <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucune donnée pour cette période.</p>
      </div>
    );
  }
  return (
    <div style={{ margin: '0 0 16px' }}>
      {t.titre && <h4 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600 }}>{t.titre}</h4>}
      <div className="rh-conteneur-tableau">
        <table className="rh-tableau">
          <thead><tr>{t.colonnes.map((c) => <th key={c.cle} style={aDroite(c.type) ? { textAlign: 'right' } : undefined}>{c.libelle}</th>)}</tr></thead>
          <tbody>
            {t.lignes.map((l, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={l.id || l.dispositif_id || i}>
                {t.colonnes.map((c) => <td key={c.cle} style={aDroite(c.type) ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } : undefined}>{cellule(l[c.cle], c.type)}</td>)}
              </tr>
            ))}
          </tbody>
          {t.total && (
            <tfoot>
              <tr style={{ fontWeight: 600, background: 'var(--sg-fond)' }}>
                {t.colonnes.map((c, i) => <td key={c.cle} style={aDroite(c.type) ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } : undefined}>{i === 0 ? cellule(t.total[c.cle] ?? 'Total', 'texte') : (t.total[c.cle] === undefined ? '' : cellule(t.total[c.cle], c.type))}</td>)}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

/** Un bloc : titre, tableaux, paragraphes (notes du serveur). `niveau` : balise du titre. */
export function BlocRapport({ bloc, niveau = 3 }) {
  if (!bloc) return null;
  const Titre = `h${niveau}`;
  return (
    <div>
      {bloc.titre && <Titre style={{ margin: '0 0 10px', fontSize: niveau <= 2 ? 17 : 15, fontWeight: 600 }}>{bloc.titre}</Titre>}
      {bloc.tableaux.map((t, i) => <TableauRapport key={t.titre || i} t={t} />)}
      {bloc.paragraphes.length > 0 && (
        <ul style={{ margin: '0 0 8px 18px', padding: 0, fontSize: 13, color: 'var(--sg-texte-2)' }}>
          {bloc.paragraphes.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
    </div>
  );
}

/** Contrôles (équations) : chaque ligne dit « Juste » ou « Faux » en toutes lettres. */
export function Controles({ controles, titre = 'Contrôles (équations vérifiées par le serveur)', avecSection = false }) {
  const l = Array.isArray(controles) ? controles : [];
  if (!l.length) return <p className="rh-cellule-secondaire" style={{ margin: '0 0 8px' }}>Aucun contrôle pour cet état.</p>;
  const faux = l.filter((c) => !c.juste).length;
  return (
    <div style={{ margin: '0 0 12px' }} data-controles={faux ? 'faux' : 'justes'}>
      <h4 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600 }}>{titre}</h4>
      <p style={{ margin: '0 0 8px', fontSize: 14 }}>
        {faux ? <strong style={{ color: 'var(--sg-danger)' }}>{faux} contrôle(s) faux sur {l.length} : écart à expliquer avant tout arrêt.</strong>
          : <strong style={{ color: 'var(--sg-succes)' }}>Tous les contrôles sont justes ({l.length}).</strong>}
      </p>
      <div className="rh-conteneur-tableau">
        <table className="rh-tableau">
          <thead><tr>{avecSection && <th>Section</th>}<th>Contrôle</th><th style={{ textAlign: 'right' }}>Membre gauche</th><th style={{ textAlign: 'right' }}>Membre droit</th><th>Résultat</th></tr></thead>
          <tbody>
            {l.map((c) => (
              <tr key={`${c.section || ''}-${c.code}`}>
                {avecSection && <td>{LIBELLES_ETATS[c.section] || (c.section === 'campagne' ? 'Campagne' : c.section)}</td>}
                <td>{c.libelle}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{nombre(c.gauche)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{nombre(c.droite)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {c.juste
                    ? <span style={{ color: 'var(--sg-succes)', display: 'inline-flex', gap: 4, alignItems: 'center', fontWeight: 600 }}><CheckCircle2 size={15} aria-hidden="true" />Juste</span>
                    : <span style={{ color: 'var(--sg-danger)', display: 'inline-flex', gap: 4, alignItems: 'center', fontWeight: 600 }}><XCircle size={15} aria-hidden="true" />Faux</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Exports Excel / PDF / HTML : `exporter(format)` → { blob, nom, empreinte }. */
export function BoutonsExport({ exporter, libelle = 'Exporter' }) {
  const [enCours, setEnCours] = useState('');
  const [erreur, setErreur] = useState(null);
  const [info, setInfo] = useState('');
  const lancer = async (f) => {
    setEnCours(f); setErreur(null); setInfo('');
    try {
      const x = await exporter(f);
      ouvrirBlob(x, x.nom);
      setInfo(`Fichier ${x.nom} téléchargé${x.empreinte ? ` — empreinte ${x.empreinte.slice(0, 16)}…` : ''}. L'export est tracé.`);
    } catch (e) { setErreur(e.normalisee || { statut: 0, code: null, message: 'Export impossible.', erreurs: [] }); }
    setEnCours('');
  };
  return (
    <div style={{ margin: '0 0 12px' }}>
      <div role="group" aria-label={libelle} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 13, color: 'var(--sg-texte-2)' }}>{libelle} :</span>
        {FORMATS.map(([f, l]) => <Bouton key={f} petit Icone={Download} disabled={!!enCours} onClick={() => lancer(f)}>{enCours === f ? '…' : l}</Bouton>)}
      </div>
      {info && <p className="rh-form-aide" style={{ margin: '4px 0 0' }} role="status">{info}</p>}
      {erreur && <div style={{ marginTop: 6 }}><ErreurServeur erreur={erreur} /></div>}
    </div>
  );
}
